import type { WorkflowStageOutDto } from 'shared/domain/workflow/types';
import {
  ApprovalType,
  getDefaultAutoApproveForApprovalType,
} from 'shared/domain/workflow/types';
import { normalizeWorkflowApprovers } from 'shared/domain/workflow/stageApprovers';
import { createUniqueId } from 'shared/utils/id/id';
import type {
  WorkflowStageRow,
  WorkflowOriginalSnapshot,
  StageCreateBody,
} from '../types';

function createLocalId(): string {
  return `wf-stage-local-${createUniqueId()}`;
}

export function serverStageToRow(stage: WorkflowStageOutDto): WorkflowStageRow {
  const approvalType = stage.approvalType ?? ApprovalType.NONE;
  const autoApprove =
    typeof stage.autoApprove === 'boolean'
      ? stage.autoApprove
      : getDefaultAutoApproveForApprovalType(approvalType);
  const approvers = normalizeWorkflowApprovers(stage.approvers);
  return {
    _localId: createLocalId(),
    _serverId: stage._id,
    name: stage.name ?? '',
    description: stage.description,
    icon: stage.icon ?? null,
    approvalType,
    autoApprove,
    approvers,
    requiredApprovals: deriveInitialRequiredApprovals(stage.requiredApprovals, approvalType, approvers.length),
    deadlineDays: stage.deadlineDays,
  };
}

export function createEmptyStageRow(template?: Partial<WorkflowStageRow>): WorkflowStageRow {
  const approvalType = template?.approvalType ?? ApprovalType.NONE;
  return {
    _localId: createLocalId(),
    _serverId: undefined,
    name: '',
    description: undefined,
    icon: template?.icon ?? null,
    approvalType,
    autoApprove: getDefaultAutoApproveForApprovalType(approvalType),
    approvers: [],
    requiredApprovals: null,
    deadlineDays: template?.deadlineDays,
  };
}

export function buildStageCreatePayload(row: WorkflowStageRow): StageCreateBody {
  const autoApprove = getDefaultAutoApproveForApprovalType(row.approvalType);
  const approvers = autoApprove ? [] : normalizeWorkflowApprovers(row.approvers);
  const payload: StageCreateBody = {
    name: row.name.trim(),
    icon: row.icon,
    approvalType: row.approvalType,
    approvers,
  };
  // n-of-x only makes sense for PARALLEL approval. SEQUENTIAL has no threshold
  // concept (every approver decides in order) and NONE has no approvers at all.
  if (row.approvalType === ApprovalType.PARALLEL && approvers.length > 0) {
    payload.requiredApprovals = row.requiredApprovals ?? approvers.length;
  }
  return payload;
}

function deriveInitialRequiredApprovals(
  fromServer: number | null | undefined,
  approvalType: ApprovalType,
  approversCount: number
): number | null {
  // Only PARALLEL stages carry an n-of-x threshold. SEQUENTIAL and NONE
  // intentionally keep requiredApprovals null.
  if (approvalType !== ApprovalType.PARALLEL) return null;
  if (typeof fromServer === 'number' && fromServer > 0) {
    return Math.min(fromServer, approversCount || fromServer);
  }
  return approversCount > 0 ? approversCount : null;
}

export function buildOriginalSnapshot(
  workflowName: string,
  stages: readonly WorkflowStageOutDto[]
): WorkflowOriginalSnapshot {
  const activeStages = stages.filter((s) => !s.deleted);
  return {
    name: workflowName,
    stagesByServerId: new Map(activeStages.map((s) => [s._id, s])),
    originalOrderServerIds: activeStages.map((s) => s._id),
  };
}
